<template>
  <div
    class="navbar-wrapper"
    :class="{ 'navbar-wrapper_sticky': sticky }"
  >
    <Wrap
      ref="navbarNode"
      class="nav"
    >
      <template v-if="!loading">
        <div
          v-for="navEl in nav"
          :key="navEl.dataset"
          :class="['nav-el', `nav-el-${navEl.dataset}`, { 'nav-el-active': dataSet === navEl.dataset }]"
          @click="goTo(navEl.dataset)"
        >
          {{ navEl.title }}
        </div>
      </template>
      <template v-else>
        <SkeletonItem
          v-for="el in 3"
          :key="el"
          class="skeleton"
        />
      </template>
    </Wrap>
  </div>
</template>

<script setup lang="ts">
  import { SkeletonItem } from '@experience/core-skeleton-lib';
  import { onMounted, nextTick, inject, ref, onDeactivated, useTemplateRef, watch } from 'vue';
  import { useRoute } from 'vue-router';

  import type { NavItem } from '@/features/navigationBar/models';
  import Wrap from '@/shared/ui/Wrap.vue';

  interface Props {
    /**
     * Список элементов меню навигации.
     */
    nav: NavItem[];

    /**
     * Фиксировать в верхней части страницы при прокрутке.
     */
    sticky?: boolean;

    /**
     * Флаг индикации загрузки.
     *
     * @default false
     */
    loading?: boolean;

    /**
     * Дополнительный отступ сверху при прокрутке.
     *
     * @default 0
     */
    scrollOffset?: number;

    /**
     * Время задержки перед первичной прокруткой до выбранной секции (мс).
     *
     * @default 500
     */
    initialScrollTimeout?: number;

    /**
     * Индекс поднятия блока.
     *
     * @default 4
     */
    zIndex?: number;

    /**
     * Элемент, с которым проверяется пересечение областей контента.
     */
    rootElement?: Element | Document | null;
  }

  const props = withDefaults(defineProps<Props>(), {
    sticky: true,
    loading: false,
    scrollOffset: 0,
    initialScrollTimeout: 500,
    zIndex: 4,
    rootElement: null,
  });

  const emit = defineEmits<{
    'section-selected': [dataset: string];
  }>();

  const shadowRoot = inject<ShadowRoot>('shadowRoot')!;

  const route = useRoute();

  const navbar = useTemplateRef('navbarNode');

  let predefinedSection: NavItem | undefined;

  onMounted(() => {
    // TODO: нужно избавится от setTimeout
    setTimeout(createObs, 1000);

    predefinedSection = props.nav.find((navItem) => `#${navItem.dataset}` === route.hash);
  });

  onDeactivated(() => {
    unObserve();
  });

  const stopInitialWatching = watch(
    () => props.loading,
    () => {
      if (!props.loading && predefinedSection) {
        stopInitialWatching();
        setTimeout(() => goTo(predefinedSection!.dataset, true), props.initialScrollTimeout);
      }
    },
  );

  const nodes = ref<NodeListOf<HTMLElement> | null>(null);
  const nodeExp = ref<HTMLElement | null>(null);
  const dataSet = ref<string>(props.nav[0].dataset ?? '');
  const observer = ref<IntersectionObserver | null>(null);
  const observerExp = ref<IntersectionObserver | null>(null);

  const initObs = (options: IntersectionObserverInit): IntersectionObserver => {
    return new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry: IntersectionObserverEntry) => {
        if (entry.isIntersecting) {
          dataSet.value = (entry.target as HTMLElement).dataset.attr as string;
          const nodeToScroll = shadowRoot.querySelector(`.nav-el-${dataSet.value}`) as HTMLElement;

          if (navbar.value) {
            navbar.value.$el.scrollLeft = nodeToScroll?.offsetLeft ?? 0;
          }
        }
      });
    }, options);
  };

  const createObs = (): void => {
    nextTick(() => {
      nodes.value = shadowRoot.querySelectorAll('.obs-el');

      const options: IntersectionObserverInit = {
        root: props.rootElement,
        rootMargin: '0px',
        threshold: 0.1,
      };

      observer.value = initObs(options);

      nodes.value.forEach((node: HTMLElement) => {
        observer.value?.observe(node);
      });
    });
  };

  const unObserve = (): void => {
    if (nodes.value) {
      nodes.value.forEach((i: HTMLElement) => {
        if (observer.value) {
          observer.value.unobserve(i);
        }
      });
    }

    if (nodeExp.value && observerExp.value) {
      observerExp.value.unobserve(nodeExp.value);
    }
  };

  const goTo = (id: string, automative = false): void => {
    const targetElement = shadowRoot.getElementById(id);

    if (!targetElement) {
      return;
    }

    if (!automative) {
      emit('section-selected', id);
    }

    const navbarHeight = navbar.value?.$el.getBoundingClientRect().height ?? 40;

    const elementPosition = targetElement.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - props.scrollOffset - navbarHeight;

    dataSet.value = id;
    document.location.hash = id;
    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
  };
</script>

<style lang="scss" scoped>
  .navbar-wrapper {
    width: 100%;
    border-bottom: 1px solid var(--alias-color-border-default);

    &_sticky {
      position: sticky;
      top: 0;
      z-index: v-bind('$props.zIndex');
      background: var(--alias-color-background-default);
    }
  }

  .nav {
    display: flex;
    overflow-x: scroll;
    background: var(--alias-color-background-default);
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  .nav-el {
    padding: var(--alias-space-12) var(--alias-space-8);
    color: var(--alias-color-text-subtle);
    white-space: nowrap;
    cursor: pointer;

    @include base-400-300-regular;
  }

  .nav-el-active {
    border-bottom: 2px solid var(--alias-color-border-focused);
    color: var(--alias-color-text-default);
  }

  .skeleton {
    width: 100px;
    height: 22px;
    margin: var(--alias-space-12) var(--alias-space-8);
    border-radius: var(--alias-radius-8);
  }
</style>
